/**
 * Cart Integration — Auto-detect e-commerce platform and add items to cart
 *
 * Supports:
 * - Shopify (via /cart/add.js — variant IDs, no CSRF needed)
 * - BigCommerce (via Storefront Cart API — product IDs, session cookies)
 * - WooCommerce (via legacy AJAX — product IDs, no nonce needed for simple products)
 * - Callback fallback (host site registers window.PromptMySite.onCheckout)
 *
 * Priority: callback > auto-detected platform > null (AI message fallback)
 */

import type { ListItem } from './list-store';
import { warn, debug } from './logger';

// =============================================================================
// TYPES
// =============================================================================

export type CartPlatform = 'shopify' | 'woocommerce' | 'bigcommerce' | 'wix' | 'webflow' | 'unknown';

export interface CartResult {
  success: boolean;
  /** Whether to clear the list store after success */
  clear?: boolean;
  /** Optional status message */
  message?: string;
}

// Shopify global shape (subset we use)
interface ShopifyGlobal {
  routes?: { root?: string };
  currency?: { active?: string };
  shop?: string;
}

// WooCommerce global shape
interface WcAddToCartParams {
  wc_ajax_url?: string;
}

// =============================================================================
// PLATFORM DETECTION
// =============================================================================

/** Platform set from workspace config (takes priority over runtime detection) */
let configuredPlatform: CartPlatform | null = null;

/**
 * Set the e-commerce platform from workspace config.
 * Called during widget init when the backend provides ecommercePlatform.
 */
export function setPlatformFromConfig(platform: string): void {
  if (platform === 'wix' || platform === 'shopify' || platform === 'woocommerce' || platform === 'bigcommerce' || platform === 'webflow') {
    configuredPlatform = platform;
    debug('[Cart] Platform set from config:', platform);
  }
}

/**
 * Get the current cart platform (for UI decisions like button labels).
 */
export function getCartPlatform(): CartPlatform {
  return configuredPlatform ?? detectPlatform();
}

/**
 * Detect which e-commerce platform the host site runs on.
 * Checks for well-known globals that each platform injects.
 */
export function detectPlatform(): CartPlatform {
  // Config-based platform takes priority
  if (configuredPlatform) return configuredPlatform;

  // Shopify: window.Shopify is always present on Shopify storefronts
  if (typeof (window as unknown as { Shopify?: ShopifyGlobal }).Shopify !== 'undefined') {
    return 'shopify';
  }

  // WooCommerce: localized script globals
  if (typeof (window as unknown as { wc_add_to_cart_params?: WcAddToCartParams }).wc_add_to_cart_params !== 'undefined') {
    return 'woocommerce';
  }

  // Fallback WooCommerce check: body class or plugin scripts
  if (document.body?.classList.contains('woocommerce') ||
      document.querySelector('script[src*="woocommerce"]') !== null) {
    return 'woocommerce';
  }

  // BigCommerce: check for BCData global or stencil utils script
  if (typeof (window as unknown as Record<string, unknown>).BCData !== 'undefined' ||
      document.querySelector('script[src*="stencil-utils"]') !== null ||
      document.querySelector('script[src*="bigcommerce.com"]') !== null) {
    return 'bigcommerce';
  }

  // Webflow: check for data-wf-site attribute or webflow.js script
  if (document.documentElement?.hasAttribute('data-wf-site') ||
      document.querySelector('script[src*="webflow.js"]') !== null) {
    return 'webflow';
  }

  // Wix: check for Wix-specific globals or meta tags
  if (typeof (window as unknown as Record<string, unknown>).wixDevelopersAnalytics !== 'undefined' ||
      document.querySelector('meta[name="generator"][content*="Wix"]') !== null ||
      document.querySelector('script[src*="parastorage.com"]') !== null) {
    return 'wix';
  }

  return 'unknown';
}

// =============================================================================
// SHOPIFY ADAPTER
// =============================================================================

/**
 * Convert a product title to a Shopify handle.
 * Shopify handles are lowercase, hyphenated, alphanumeric-only.
 */
function titleToHandle(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Check if an ID looks like a real Shopify numeric ID (>1000).
 * Browse cards from the AI use simple sequential IDs like 1, 2, 3.
 */
function isLikelyShopifyId(id: string): boolean {
  const num = Number(id);
  return !isNaN(num) && num > 1000;
}

/**
 * Resolve a ListItem to a Shopify variant ID.
 * Tries multiple strategies: direct variant ID, metadata, handle lookup.
 */
async function resolveShopifyVariantId(item: ListItem, root: string): Promise<number | null> {
  // 1. Check metadata for explicit variant/product IDs
  const meta = item.metadata as Record<string, unknown> | undefined;
  debug('[Cart] Resolving variant for:', item.title, 'id:', item.id, 'metadata:', meta);

  const metaVariantId = meta?.['variant_id'] ?? meta?.['variantId'];
  if (metaVariantId && Number(metaVariantId) > 0) {
    debug('[Cart] Using metadata variant_id:', metaVariantId);
    return Number(metaVariantId);
  }
  const metaProductId = meta?.['product_id'] ?? meta?.['productId'] ?? meta?.['externalId'];

  // 2. If the item ID looks like a real Shopify ID, use it directly
  if (isLikelyShopifyId(item.id)) {
    debug('[Cart] Using item ID as variant:', item.id);
    return Number(item.id);
  }

  // 3. Try looking up by product ID from metadata
  if (metaProductId && Number(metaProductId) > 0) {
    debug('[Cart] Looking up by product ID:', metaProductId);
    const variant = await fetchFirstVariant(root, `${metaProductId}`);
    if (variant) return variant;
  }

  // 4. Try looking up by handle from metadata
  const metaHandle = meta?.['handle'] as string | undefined;
  if (metaHandle) {
    debug('[Cart] Looking up by handle from metadata:', metaHandle);
    const variant = await fetchFirstVariant(root, metaHandle);
    if (variant) return variant;
  }

  // 5. Derive handle from title and look up
  if (item.title) {
    const handle = titleToHandle(item.title);
    debug('[Cart] Looking up by derived handle:', handle);
    const variant = await fetchFirstVariant(root, handle);
    if (variant) return variant;
  }

  warn('[Cart] Could not resolve variant ID for:', item.title);
  return null;
}

/**
 * Fetch a Shopify product by ID or handle and return first available variant ID.
 */
async function fetchFirstVariant(root: string, idOrHandle: string): Promise<number | null> {
  try {
    const res = await fetch(`${root}products/${idOrHandle}.js`);
    if (!res.ok) return null;
    const product = await res.json() as {
      variants?: Array<{ id: number; available: boolean }>;
    };
    const variant = product.variants?.find(v => v.available) ?? product.variants?.[0];
    return variant?.id ?? null;
  } catch {
    return null;
  }
}

/**
 * Add items to Shopify cart via AJAX Cart API.
 * No auth/CSRF needed — same-origin cookies handle session.
 *
 * Resolves variant IDs by trying: metadata IDs, direct ID, handle lookup
 * from title. This handles both real API data and AI-generated browse cards.
 */
async function shopifyAddToCart(items: ListItem[]): Promise<CartResult> {
  const shopify = (window as unknown as { Shopify: ShopifyGlobal }).Shopify;
  const root = shopify?.routes?.root ?? '/';

  // Resolve variant IDs for all items
  const variantItems: Array<{ id: number; quantity: number }> = [];
  const failedItems: string[] = [];

  for (const item of items) {
    const variantId = await resolveShopifyVariantId(item, root);
    if (variantId) {
      variantItems.push({ id: variantId, quantity: 1 });
    } else {
      failedItems.push(item.title);
    }
  }

  if (variantItems.length === 0) {
    throw new Error(
      failedItems.length > 0
        ? `Could not find products: ${failedItems.join(', ')}`
        : 'No valid products to add to cart'
    );
  }

  const response = await fetch(`${root}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items: variantItems }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error((errorData as { description?: string }).description || 'Failed to add to cart');
  }

  // Refresh cart UI so the header count / cart drawer updates
  await refreshShopifyCart(root);

  const addedCount = variantItems.length;
  return {
    success: true,
    message: failedItems.length > 0
      ? `${addedCount} of ${items.length} items added to cart`
      : `${addedCount} item${addedCount > 1 ? 's' : ''} added to cart`,
  };
}

/**
 * Refresh the Shopify cart UI after adding items.
 *
 * Strategy (all run — different themes listen for different signals):
 * 1. Fetch /cart.js to get real cart state
 * 2. Section Rendering API — re-render cart drawer + icon (Dawn & OS 2.0 themes)
 * 3. Update cart count badges directly (works across many themes)
 * 4. Dispatch standard events that themes listen for
 *
 * See: https://shopify.dev/docs/api/ajax/reference/cart
 */
async function refreshShopifyCart(root: string): Promise<void> {
  try {
    // 1. Fetch updated cart state
    const cartRes = await fetch(`${root}cart.js`);
    if (!cartRes.ok) return;
    const cart = await cartRes.json() as { item_count: number; items: unknown[] };

    // 2. Section Rendering API (Shopify OS 2.0 themes like Dawn, Horizon)
    // Fetch fresh HTML for cart-related sections and hot-swap them
    try {
      const sectionIds = [
        'cart-icon-bubble',
        'cart-drawer',
        'cart-notification-product',
        'cart-notification-button',
      ];
      const sectionsRes = await fetch(`${root}?sections=${sectionIds.join(',')}`);
      if (sectionsRes.ok) {
        const sections = await sectionsRes.json() as Record<string, string>;
        for (const [sectionId, html] of Object.entries(sections)) {
          if (!html) continue;
          const target = document.getElementById(`shopify-section-${sectionId}`);
          if (target) {
            const parsed = new DOMParser().parseFromString(html, 'text/html');
            target.innerHTML = parsed.body.innerHTML;
          }
        }
      }
    } catch {
      // Section API not available — fall through to manual update
    }

    // Also try refreshing <cart-drawer> custom element (Dawn/Horizon specific)
    try {
      const drawerSectionsRes = await fetch(`${root}cart?sections=cart-drawer`);
      if (drawerSectionsRes.ok) {
        const sections = await drawerSectionsRes.json() as Record<string, string>;
        const drawerHtml = sections['cart-drawer'];
        if (drawerHtml) {
          const parsed = new DOMParser().parseFromString(drawerHtml, 'text/html');
          const sourceDrawer = parsed.querySelector('cart-drawer');
          const targetDrawer = document.querySelector('cart-drawer');
          if (targetDrawer && sourceDrawer) {
            targetDrawer.innerHTML = sourceDrawer.innerHTML;
            targetDrawer.classList.remove('is-empty');
          }
        }
      }
    } catch {
      // cart-drawer not present — ignore
    }

    // 3. Update common cart count elements (works across many themes)
    const countSelectors = [
      '.cart-count-bubble span[aria-hidden]',
      '.cart-count-bubble',
      '.cart-count',
      '[data-cart-count]',
      '.js-cart-count',
      '.site-header__cart-count',
      '.header__cart-count',
      '#cart-icon-bubble .icon-bubble',
    ];
    for (const sel of countSelectors) {
      document.querySelectorAll(sel).forEach(el => {
        const htmlEl = el as HTMLElement;
        if (htmlEl.dataset?.['cartCount'] !== undefined) {
          htmlEl.dataset['cartCount'] = String(cart.item_count);
        } else {
          htmlEl.textContent = String(cart.item_count);
        }
        htmlEl.hidden = cart.item_count === 0;
      });
    }

    // 4. Dispatch events that various themes listen for
    // Dawn listens for 'cart:refresh', Horizon for 'cart:update'
    const eventDetail = { detail: { cart, data: { itemCount: cart.item_count, source: 'pms-widget' } } };
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, ...eventDetail }));
    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, ...eventDetail }));
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, ...eventDetail }));
    // Some themes listen on documentElement instead of document
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, ...eventDetail }));
  } catch {
    // Best-effort — don't block the success response
  }
}

// =============================================================================
// BIGCOMMERCE ADAPTER
// =============================================================================

/**
 * Add items to BigCommerce cart via Storefront Cart API.
 * No auth token needed — runs on the storefront origin with session cookies.
 *
 * API: POST /api/storefront/carts (create) or POST /api/storefront/carts/{id}/items (add)
 * Docs: https://developer.bigcommerce.com/docs/storefront/cart-checkout/cart
 */
async function bigcommerceAddToCart(items: ListItem[]): Promise<CartResult> {
  // 1. Get or create a cart
  let cartId = await getBigCommerceCartId();

  const lineItems = items.map(item => {
    const meta = item.metadata as Record<string, unknown> | undefined;
    const productId = Number(meta?.['product_id'] ?? meta?.['productId'] ?? meta?.['externalId'] ?? item.id);

    // Resolve variant_id: direct field first, then fall back to first variant in _variants array.
    // Backend stores variant data as metadata._variants = [{ id, sku, price, choices }, ...].
    let variantId = Number(meta?.['variant_id'] ?? meta?.['variantId'] ?? 0);
    if (variantId <= 0 && meta?.['_variants']) {
      const variants = meta['_variants'] as Array<{ id?: number }>;
      if (Array.isArray(variants) && variants.length > 0) {
        variantId = Number(variants[0]?.id ?? 0);
      }
    }

    return { productId, variantId, quantity: 1 };
  });

  const validItems = lineItems.filter(li => li.productId > 0);
  if (validItems.length === 0) {
    throw new Error('No valid products to add to cart');
  }

  // BigCommerce Storefront Cart API expects camelCase keys
  const body = validItems.map(li => {
    const entry: Record<string, unknown> = { quantity: li.quantity, productId: li.productId };
    if (li.variantId > 0) entry['variantId'] = li.variantId;
    return entry;
  });

  let response: Response;
  if (cartId) {
    // Add to existing cart
    response = await fetch(`/api/storefront/carts/${cartId}/items`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lineItems: body }),
    });
  } else {
    // Create new cart with items
    response = await fetch('/api/storefront/carts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lineItems: body }),
    });
  }

  if (!response.ok) {
    // Cart may have expired — retry with a fresh cart (404 = not found, 422 = invalid cart)
    if (cartId && (response.status === 404 || response.status === 422)) {
      debug('[Cart] BigCommerce cart expired or invalid, creating new one');
      cartId = null;
      try { sessionStorage.removeItem('pms_bc_cart_id'); } catch { /* ignore */ }
      response = await fetch('/api/storefront/carts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lineItems: body }),
      });
    }
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      const detail = (err['detail'] ?? err['title'] ?? 'Failed to add to cart') as string;
      debug('[Cart] BigCommerce error:', response.status, err);
      throw new Error(detail);
    }
  }

  // Update cart count badge
  try {
    const cart = await response.json() as { id?: string; lineItems?: { physicalItems?: unknown[] } };
    if (cart.id) {
      // Store cart ID for future add-to-cart calls
      try { sessionStorage.setItem('pms_bc_cart_id', cart.id); } catch { /* ignore */ }
    }
    // Update common BigCommerce cart count elements
    const itemCount = (cart.lineItems?.physicalItems ?? []).length;
    document.querySelectorAll('.cart-quantity, [data-cart-quantity], .navUser-item--cart .countPill--positive').forEach(el => {
      (el as HTMLElement).textContent = String(itemCount);
    });
  } catch { /* best effort */ }

  return {
    success: true,
    message: `${validItems.length} item${validItems.length > 1 ? 's' : ''} added to cart`,
  };
}

/** Get existing BigCommerce cart ID from session or storefront API. */
async function getBigCommerceCartId(): Promise<string | null> {
  // Check sessionStorage first
  try {
    const stored = sessionStorage.getItem('pms_bc_cart_id');
    if (stored) return stored;
  } catch { /* ignore */ }

  // Fetch current cart from storefront API
  try {
    const res = await fetch('/api/storefront/carts');
    if (!res.ok) return null;
    const carts = await res.json() as Array<{ id: string }>;
    const id = carts?.[0]?.id ?? null;
    if (id) {
      try { sessionStorage.setItem('pms_bc_cart_id', id); } catch { /* ignore */ }
    }
    return id;
  } catch {
    return null;
  }
}

// =============================================================================
// WOOCOMMERCE ADAPTER
// =============================================================================

/**
 * Add items to WooCommerce cart via legacy AJAX API.
 * No nonce needed for simple products.
 * Uses product IDs directly (card's ID works).
 */
async function woocommerceAddToCart(items: ListItem[]): Promise<CartResult> {
  const wcParams = (window as unknown as { wc_add_to_cart_params?: WcAddToCartParams }).wc_add_to_cart_params;
  const ajaxUrl = wcParams?.wc_ajax_url?.replace('%%endpoint%%', 'add_to_cart')
    ?? '/?wc-ajax=add_to_cart';

  let addedCount = 0;
  let lastError = '';

  // WooCommerce legacy AJAX adds one product at a time
  for (const item of items) {
    try {
      const body = new URLSearchParams({
        product_id: String(item.id),
        quantity: '1',
      });

      const response = await fetch(ajaxUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const data = await response.json() as { error?: boolean; fragments?: unknown; cart_hash?: string };

      if (data.error) {
        lastError = 'Some items require variant selection';
        continue;
      }

      addedCount++;

      // Dispatch WooCommerce cart update event so mini-carts refresh
      document.body.dispatchEvent(
        new CustomEvent('added_to_cart', {
          detail: [data.fragments, data.cart_hash, null],
        })
      );
    } catch {
      lastError = 'Network error adding to cart';
    }
  }

  if (addedCount === 0) {
    throw new Error(lastError || 'Failed to add items to cart');
  }

  return {
    success: true,
    message: addedCount === items.length
      ? `${addedCount} item${addedCount > 1 ? 's' : ''} added to cart`
      : `${addedCount} of ${items.length} items added to cart`,
  };
}

// =============================================================================
// PUBLIC API
// =============================================================================

/**
 * Add items to the host site's cart.
 * Auto-detects platform and calls the appropriate API.
 *
 * Returns null if no platform detected (caller should fall back to AI message).
 */
export async function addToCart(items: ListItem[]): Promise<CartResult | null> {
  if (items.length === 0) {
    return { success: false, message: 'No items to add' };
  }

  const platform = detectPlatform();

  try {
    switch (platform) {
      case 'shopify':
        return await shopifyAddToCart(items);
      case 'woocommerce':
        return await woocommerceAddToCart(items);
      case 'bigcommerce':
        return await bigcommerceAddToCart(items);
      case 'wix':
      case 'webflow':
        return null; // Wix/Webflow use "Visit Product" links instead of server-side checkout
      default:
        return null; // No platform detected — caller handles fallback
    }
  } catch (err) {
    warn('Cart integration error:', err);
    throw err; // Re-throw for caller to handle UI feedback
  }
}
